import React, { useState } from 'react';
import { useStore } from '../../../store/useStore';
import { Package, UploadCloud, ChevronRight, Image as ImageIcon, Sparkles, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { UploadModal } from '../../ui/UploadModal';

export const ProdutosResultados: React.FC = () => {
  const { products, mediaLibrary, setMediaLibrary } = useStore();
  const [activeProdutoId, setActiveProdutoId] = useState<string | null>(products?.[0]?.id || null);
  const [isUploadLimpaOpen, setIsUploadLimpaOpen] = useState(false);
  const [isUploadCasoOpen, setIsUploadCasoOpen] = useState(false);

  const activeProduct = products?.find(p => p.id === activeProdutoId);

  const mockCases = [
    { id: '1', type: 'caso', produtoId: activeProdutoId, title: 'Projeto Residencial - Sr. João', antes: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=400&q=80', depois: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=400&q=80' },
    { id: '2', type: 'caso', produtoId: activeProdutoId, title: 'Reforma Cozinha', antes: 'https://images.unsplash.com/photo-1556912173-3bb406ef7e77?auto=format&fit=crop&w=400&q=80', depois: 'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?auto=format&fit=crop&w=400&q=80' },
  ];

  const allFotos = mediaLibrary.fotosProdutos?.length > 0 ? mediaLibrary.fotosProdutos : mockCases;
  
  const fotosLimpas = allFotos.filter((f: any) => f.type === 'limpa' && f.produtoId === activeProdutoId);
  const casos = allFotos.filter((f: any) => f.type === 'caso' && f.produtoId === activeProdutoId);

  const handleUploadLimpa = (files: File[], title: string) => {
    if (files.length > 0 && activeProdutoId) {
      const newFotos = files.map(file => ({
        id: crypto.randomUUID().toString(36).substr(2, 9),
        produtoId: activeProdutoId,
        type: 'limpa',
        url: URL.createObjectURL(file)
      }));
      setMediaLibrary({ fotosProdutos: [...allFotos, ...newFotos] });
      toast.success(`${newFotos.length} foto(s) limpa(s) adicionada(s)!`);
    }
  };

  const handleUploadCaso = (files: File[], title: string) => {
    if (files.length > 0 && activeProdutoId) {
       const file = files[0];
       const newCaso = {
         id: crypto.randomUUID().toString(36).substr(2, 9),
         produtoId: activeProdutoId,
         type: 'caso',
         title: title || 'Novo Caso de Estudo',
         antes: URL.createObjectURL(file), // mock using same image
         depois: URL.createObjectURL(file) // mock using same image
       };
       setMediaLibrary({ fotosProdutos: [...allFotos, newCaso] });
       toast.success(`Novo caso criado com sucesso!`);
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <UploadModal 
        isOpen={isUploadLimpaOpen}
        onClose={() => setIsUploadLimpaOpen(false)}
        title="Enviar Fotos Limpas"
        accept="image/*"
        multiple
        onUpload={handleUploadLimpa}
      />
      <UploadModal 
        isOpen={isUploadCasoOpen}
        onClose={() => setIsUploadCasoOpen(false)}
        title="Novo Caso / Antes e Depois"
        accept="image/*"
        onUpload={handleUploadCaso}
      />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Produtos & Resultados</h3>
          <p className="text-sm text-gray-500 mt-1">Conecte fotos limpas e casos de &quot;Antes e Depois&quot; aos seus produtos.</p>
        </div>
      </div>

      <div className="flex gap-6 h-[500px]">
        {/* Sidebar Products */}
        <div className="w-64 shrink-0 flex flex-col gap-1 border-r border-gray-100 pr-6">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-3">Seus Produtos/Serviços</h4>
          {products?.length === 0 && (
            <p className="text-xs text-gray-500 px-3">Nenhum produto cadastrado no "Meu Negócio".</p>
          )}
          {products?.map(prod => (
            <button
              key={prod.id}
              onClick={() => setActiveProdutoId(prod.id)}
              className={`flex items-center justify-between px-3 py-3 rounded-xl text-sm transition-colors text-left ${activeProdutoId === prod.id ? 'bg-indigo-50 border border-indigo-100 shadow-sm' : 'hover:bg-gray-50 border border-transparent'}`}
            >
              <div>
                <div className={`font-medium line-clamp-1 ${activeProdutoId === prod.id ? 'text-indigo-900' : 'text-gray-700'}`}>{prod.name}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{prod.price}</div>
              </div>
              <ChevronRight size={14} className={activeProdutoId === prod.id ? 'text-indigo-500' : 'text-gray-300'} />
            </button>
          ))}
        </div>

        {/* Product Details Content */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {activeProduct ? (
            <div className="space-y-8">
              {/* Top Banner / Info */}
              <div className="bg-gradient-to-r from-gray-900 to-indigo-900 rounded-2xl p-6 text-white flex justify-between items-center">
                <div>
                  <h4 className="text-lg font-bold">{activeProduct.name}</h4>
                  <p className="text-sm text-indigo-200 mt-1">{activeProduct.price}</p>
                </div>
                <Package size={32} className="text-white/20" />
              </div>

              {/* Fotos Limpas do Produto */}
              <div>
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ImageIcon size={16} className="text-indigo-500" />
                  Fotos Limpas (E-commerce / Catálogo)
                </h5>
                <div className="flex flex-wrap gap-4">
                  {fotosLimpas.map((foto: any) => (
                    <div key={foto.id} className="w-32 h-32 rounded-xl overflow-hidden border border-gray-200">
                       <img src={foto.url} alt="Produto" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  <div onClick={() => setIsUploadLimpaOpen(true)} className="w-32 h-32 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center bg-gray-50/50 hover:bg-gray-50 transition-colors cursor-pointer group">
                     <UploadCloud size={20} className="text-gray-400 mb-1 group-hover:text-indigo-500 transition-colors" />
                     <span className="text-[10px] font-medium text-gray-500">Adicionar Foto</span>
                  </div>
                </div>
              </div>

              {/* Casos / Antes e Depois */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles size={16} className="text-amber-500" />
                    Casos Clínicos / Antes & Depois
                  </h5>
                  <button onClick={() => setIsUploadCasoOpen(true)} className="text-xs bg-black text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1">
                    <Plus size={14} /> Novo Caso
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {casos.map((caso: any) => (
                    <div key={caso.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:border-indigo-200 transition-colors">
                      <h6 className="text-sm font-semibold text-gray-900 mb-3">{caso.title}</h6>
                      <div className="flex gap-2 h-24">
                        <div className="flex-1 rounded-lg overflow-hidden relative">
                          <img src={caso.antes} alt="Antes" className="w-full h-full object-cover grayscale opacity-80" />
                          <div className="absolute top-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider">Antes</div>
                        </div>
                        <div className="flex-1 rounded-lg overflow-hidden relative">
                          <img src={caso.depois} alt="Depois" className="w-full h-full object-cover" />
                          <div className="absolute top-1 left-1 bg-indigo-600/90 text-white text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider">Depois</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {casos.length === 0 && (
                    <div className="col-span-full py-8 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                       <p className="text-sm text-gray-500">Nenhum caso cadastrado para este produto.</p>
                       <button onClick={() => setIsUploadCasoOpen(true)} className="mt-2 text-indigo-600 text-sm font-medium hover:underline">Adicionar o primeiro caso</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center text-gray-500">
              <Package size={48} className="text-gray-200 mb-4" />
              <p>Selecione ou cadastre um produto para vincular mídias.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
